/**
 * Two-factor authentication — the card, and the dialogs behind it.
 *
 * Visual Reference: `design/screens/settings-profile.html`, drawn by Story
 * 24.1's round-trip: the card Off and `on`, and the dialogs `enrol`,
 * `enrol-scan`, `codes`, `codes-new` and `disable`. Until then the enrolment
 * was built to `DESIGN-SYSTEM.md` §2's Modal spec and drawn nowhere
 * (DECISION-046); it now matches its drawing (Story 24.3).
 *
 * THE RECOVERY CODES ARE SHOWN EXACTLY ONCE, wherever they are issued. No route
 * reads them back (RULE-049 — a recovery code is a credential), which is why the
 * panel says so plainly, keeps them on screen until the person says they are
 * saved, and offers Copy rather than a download (DECISION-046). They are also
 * EXPLAINED where they are issued — what they are for, that each works once, and
 * how to use one at log in — because the person reading them is the one who
 * will need them on the day the phone is gone.
 *
 * A RECOVERY CODE MAY STAND IN FOR THE PHONE'S CODE, beside the password, when
 * turning two-factor off or getting new codes (DECISION-170): the only way off a
 * lost phone that needs nobody awake.
 *
 * ENROLMENT IS OPEN AGAIN since Story 24.2 (2026-09-23): DECISION-156 hid the
 * button until the log in could ask for the code, and that story built the step.
 *
 * Stories 4.4, 24.2 and 24.3.
 */
import { useState } from 'react'
import { AuthAction } from '../../auth/AuthPanel'
import { apiDelete, apiPost, ApiFailure } from '../../lib/api'
import { Button } from '../../ui/Button'
import { Field } from '../../ui/Field'
import { Modal } from '../../ui/Modal'
import { Pill } from '../../ui/Pill'
import { QrCode } from '../../ui/QrCode'

type Enrolment = { totpURI: string; backupCodes: string[] }
type Factor = 'code' | 'recovery'

export function TwoFactorCard({
  enabled,
  onChanged,
}: {
  enabled: boolean
  /** Announces through the profile page's toast, in the button's own verb. */
  onChanged: (message: string, enabled: boolean) => void
}) {
  const [dialog, setDialog] = useState<null | 'enrol' | 'codes' | 'disable'>(null)
  const close = () => setDialog(null)

  return (
    <>
      <section className="flex max-w-mdx-measure-60 flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
        <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10">
          <h2 className="m-0 text-mdx-17 font-mdx-semibold tracking-mdx-h3">
            Two-factor authentication
          </h2>
          <Pill tone={enabled ? 'brand' : 'neutral'} dot>
            {enabled ? 'On' : 'Off'}
          </Pill>
        </div>
        <p className="m-0 max-w-mdx-measure-60 text-mdx-15 text-mdx-slate">
          A second code from your phone means a stolen password isn't enough to read your leads.
        </p>
        {enabled ? (
          // `settings-profile.html`, `on`: the two things a person with it on can
          // do, on one row that wraps at 375px.
          <div className="flex flex-wrap gap-mdx-12">
            <Button variant="secondary" onClick={() => setDialog('codes')}>
              Get new recovery codes
            </Button>
            <Button variant="danger-outline" onClick={() => setDialog('disable')}>
              Turn off two-factor authentication
            </Button>
          </div>
        ) : (
          <Button className="self-start" onClick={() => setDialog('enrol')}>
            Turn on two-factor authentication
          </Button>
        )}
      </section>

      {dialog === 'enrol' ? (
        <EnrolModal
          onClose={close}
          onDone={() => {
            close()
            onChanged('Two-factor is on', true)
          }}
        />
      ) : null}

      {dialog === 'codes' ? <NewCodesModal onClose={close} /> : null}

      {dialog === 'disable' ? (
        <DisableModal
          onClose={close}
          onDone={() => {
            close()
            onChanged('Two-factor is off', false)
          }}
        />
      ) : null}
    </>
  )
}

/**
 * Three steps in one dialog: prove it is you, scan it and save the codes, prove
 * it works. `Continue` replaces the dialog's contents rather than stacking a
 * second one, as `enrol-scan` draws it.
 *
 * The confirming code is what activates it — `services/auth` leaves
 * `skipVerificationOnEnable` off on purpose, so somebody who scans the QR and
 * then loses the phone is not locked out of their own account.
 */
function EnrolModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const start = async () => {
    setBusy(true)
    setError(null)
    try {
      setEnrolment(await apiPost<Enrolment>('/v1/me/2fa', { password }))
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await apiPost('/v1/me/2fa', { code })
      onDone()
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  if (!enrolment) {
    return (
      <Modal
        title="Turn on two-factor authentication"
        subtitle="Confirm it's you, and we'll set up a code from your phone."
        onClose={onClose}
        closeMark={false}
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={password.length === 0}
              loading={busy ? 'Checking' : undefined}
              onClick={() => void start()}
            >
              Continue
            </Button>
          </>
        }
      >
        <Field
          label="Your password"
          type="password"
          value={password}
          autoComplete="current-password"
          error={error ?? undefined}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Modal>
    )
  }

  const secret = new URLSearchParams(enrolment.totpURI.split('?')[1] ?? '').get('secret') ?? ''

  return (
    <Modal
      title="Scan this with your authenticator app"
      subtitle="Then enter the six-digit code it shows, to prove it worked."
      onClose={onClose}
      closeMark={false}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={code.trim().length !== 6}
            loading={busy ? 'Confirming' : undefined}
            onClick={() => void confirm()}
          >
            Turn it on
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-start gap-x-mdx-20 gap-y-mdx-16">
        <QrCode value={enrolment.totpURI} label="Scan to add Leadgible to your authenticator app" />
        <div className="flex min-w-0 flex-1 basis-mdx-row-identity flex-col gap-mdx-8">
          <span className="text-mdx-13 text-mdx-slate">Can't scan it? Type this into the app instead.</span>
          {/* The secret is a credential the person is meant to read — the only
              place it is ever rendered, and no route returns it again. In groups
              of four, as drawn: fifty-two characters typed from a phone screen
              are four-at-a-time work. `leading-mdx-body`, 1.6, where the drawing
              sets 1.7 — the embed snippet's value, which ConnectTab builds at the
              same token (DESIGN-SYSTEM §4.16). */}
          <span className="break-words rounded-mdx-control bg-mdx-ground px-mdx-12 py-mdx-10 text-mdx-13 font-mdx-semibold leading-mdx-body tracking-mdx-eyebrow tabular-nums">
            {inFours(secret)}
          </span>
        </div>
      </div>

      <RecoveryCodes codes={enrolment.backupCodes} />

      <Field
        label="Six-digit code"
        value={code}
        inputMode="numeric"
        autoComplete="one-time-code"
        /**
         * NO `maxLength`. The attribute clips the raw value before the strip
         * below runs, so pasting "123 456" — which is how every authenticator
         * app displays it — arrives as "123" and then loses the rest. Strip,
         * then take six.
         */
        error={error ?? undefined}
        onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
      />
    </Modal>
  )
}

/** "XBQXRQF53M2J…" → "XBQX RQF5 3M2J …" — the key as `enrol-scan` draws it. */
export function inFours(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? []).join(' ')
}

/**
 * The recovery codes, where they are issued — at enrolment and as a new set.
 *
 * COPY, NOT DOWNLOAD (DECISION-046). Ten codes on screen with no way to save
 * them invites a screenshot, which lands a credential in a photo library that
 * syncs. Copy puts them on the clipboard, which is where a password manager takes
 * them from; a `.txt` download would leave the same credential sitting in
 * Downloads, unencrypted and forgotten.
 *
 * The failure is announced rather than swallowed: `navigator.clipboard` needs a
 * secure context and a permission, and a button that silently does nothing is
 * how somebody closes this panel believing the codes are saved.
 */
function RecoveryCodes({ codes }: { codes: string[] }) {
  const [copyState, setCopyState] = useState<'idle' | 'done' | 'failed'>('idle')

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopyState('done')
    } catch {
      setCopyState('failed')
    }
  }

  return (
    <div className="flex flex-col gap-mdx-12">
      <div className="flex items-start justify-between gap-mdx-16">
        <span className="min-w-0 flex-1 text-pretty text-mdx-15 font-mdx-semibold">
          Save these recovery codes somewhere safe
        </span>
        <Button variant="secondary" size="small" onClick={() => void copy()}>
          {copyState === 'done' ? 'Copied' : 'Copy codes'}
        </Button>
      </div>
      <p className="m-0 max-w-mdx-measure-52 text-pretty text-mdx-13 text-mdx-slate">
        If you lose your phone, each code gets you in once: at log in, choose{' '}
        <span className="font-mdx-semibold">Use a recovery code instead</span> and type one. This is
        the only time they are shown — nothing in Leadgible can read them back.
      </p>
      {copyState === 'failed' ? (
        <span role="alert" className="max-w-mdx-measure-52 text-mdx-13 text-mdx-error">
          Your browser wouldn't let us reach the clipboard. Select the codes below and copy them by
          hand before you continue.
        </span>
      ) : null}
      <div className="grid grid-cols-2 gap-mdx-8">
        {codes.map((code) => (
          <span
            key={code}
            className="break-words rounded-mdx-control bg-mdx-ground px-mdx-10 py-mdx-8 text-center text-mdx-13 font-mdx-semibold tabular-nums"
          >
            {code}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * The second factor a dialog asks for: the phone's six digits, or — DECISION-170
 * — one recovery code in their place, as the log in's code step offers.
 */
function SecondFactor({
  factor,
  value,
  error,
  onFactor,
  onChange,
}: {
  factor: Factor
  value: string
  error: string | undefined
  onFactor: (factor: Factor) => void
  onChange: (value: string) => void
}) {
  return factor === 'code' ? (
    <Field
      key="code"
      label="Six-digit code"
      value={value}
      inputMode="numeric"
      autoComplete="one-time-code"
      error={error}
      // NO `maxLength` — see the enrolment's field. Strip, then take six.
      onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
      action={
        <AuthAction small onClick={() => onFactor('recovery')}>
          Use a recovery code instead
        </AuthAction>
      }
    />
  ) : (
    <Field
      key="recovery"
      label="Recovery code"
      value={value}
      autoComplete="off"
      autoCapitalize="off"
      spellCheck={false}
      error={error}
      onChange={(event) => onChange(event.target.value)}
      action={
        <AuthAction small onClick={() => onFactor('code')}>
          Use your authenticator app instead
        </AuthAction>
      }
    />
  )
}

/** Both factors, as the API takes them: the phone's code, or a recovery code. */
const factorBody = (password: string, factor: Factor, value: string) =>
  factor === 'code' ? { password, code: value } : { password, recoveryCode: value.trim() }

const factorReady = (factor: Factor, value: string) =>
  factor === 'code' ? value.length === 6 : value.trim().length > 0

/** Which field a refusal belongs under: a wrong password is 401, the rest name a field. */
const refusalField = (failure: ApiFailure) =>
  failure.code === 'unauthenticated' || failure.details?.['field'] === 'password' ? 'password' : 'factor'

/**
 * A fresh set of recovery codes — Story 24.3.
 *
 * It asks for what turning two-factor off asks for, because a fresh set is as
 * powerful as the old one: the password AND a second factor. The old set stops
 * working the moment the new one exists, which the dialog says before anything
 * is pressed. The new ten are then shown ONCE, in a dialog whose only way out is
 * saying they are saved — `codes-new` draws no Cancel and no close mark.
 */
function NewCodesModal({ onClose }: { onClose: () => void }) {
  const [password, setPassword] = useState('')
  const [factor, setFactor] = useState<Factor>('code')
  const [second, setSecond] = useState('')
  const [issued, setIssued] = useState<string[] | null>(null)
  const [refusal, setRefusal] = useState<{ field: 'password' | 'factor'; message: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const issue = async () => {
    setBusy(true)
    setRefusal(null)
    try {
      const { backupCodes } = await apiPost<{ backupCodes: string[] }>(
        '/v1/me/2fa/recovery-codes',
        factorBody(password, factor, second),
      )
      setIssued(backupCodes)
    } catch (failure) {
      setRefusal(
        failure instanceof ApiFailure
          ? { field: refusalField(failure), message: failure.message }
          : { field: 'factor', message: 'Something went wrong.' },
      )
    } finally {
      setBusy(false)
    }
  }

  if (issued) {
    return (
      // A new dialog, not the last one's contents changed: keyed, so it mounts
      // afresh rather than recolouring the previous dialog's Cancel into its one
      // button mid-transition.
      <Modal
        key="issued"
        title="Your new recovery codes"
        subtitle="Your old ones stopped working just now."
        onClose={onClose}
        dismissible={false}
        actions={<Button onClick={onClose}>I've saved them</Button>}
      >
        <RecoveryCodes codes={issued} />
      </Modal>
    )
  }

  return (
    <Modal
      title="Get new recovery codes"
      subtitle="Your current codes stop working as soon as these are made. We ask for both, once."
      onClose={onClose}
      closeMark={false}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep my current codes
          </Button>
          <Button
            disabled={password.length === 0 || !factorReady(factor, second)}
            loading={busy ? 'Getting new codes' : undefined}
            onClick={() => void issue()}
          >
            Get new codes
          </Button>
        </>
      }
    >
      <Field
        label="Your password"
        type="password"
        value={password}
        autoComplete="current-password"
        error={refusal?.field === 'password' ? refusal.message : undefined}
        onChange={(event) => setPassword(event.target.value)}
      />
      <SecondFactor
        factor={factor}
        value={second}
        error={refusal?.field === 'factor' ? refusal.message : undefined}
        onFactor={(next) => {
          setFactor(next)
          setSecond('')
          setRefusal(null)
        }}
        onChange={setSecond}
      />
    </Modal>
  )
}

/**
 * Off needs BOTH factors — `services/twofactor` refuses either alone — and the
 * second may be a recovery code (DECISION-170): the way someone who has lost
 * their phone turns it off before setting it up on a new one.
 */
function DisableModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [factor, setFactor] = useState<Factor>('code')
  const [second, setSecond] = useState('')
  const [refusal, setRefusal] = useState<{ field: 'password' | 'factor'; message: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const disable = async () => {
    setBusy(true)
    setRefusal(null)
    try {
      await apiDelete('/v1/me/2fa', factorBody(password, factor, second))
      onDone()
    } catch (failure) {
      setRefusal(
        failure instanceof ApiFailure
          ? { field: refusalField(failure), message: failure.message }
          : { field: 'factor', message: 'Something went wrong.' },
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="Turn off two-factor authentication"
      subtitle="A stolen password will be enough again. We ask for both, once."
      onClose={onClose}
      closeMark={false}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep it on
          </Button>
          <Button
            variant="danger"
            disabled={password.length === 0 || !factorReady(factor, second)}
            loading={busy ? 'Turning it off' : undefined}
            onClick={() => void disable()}
          >
            Turn it off
          </Button>
        </>
      }
    >
      <Field
        label="Your password"
        type="password"
        value={password}
        autoComplete="current-password"
        error={refusal?.field === 'password' ? refusal.message : undefined}
        onChange={(event) => setPassword(event.target.value)}
      />
      <SecondFactor
        factor={factor}
        value={second}
        error={refusal?.field === 'factor' ? refusal.message : undefined}
        onFactor={(next) => {
          setFactor(next)
          setSecond('')
          setRefusal(null)
        }}
        onChange={setSecond}
      />
    </Modal>
  )
}
